import { motion } from "framer-motion";
import { Target, Smartphone, MapPin } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import SectionGlow from "./SectionGlow";
import { ease, duration, stagger } from "@/lib/motion";
const pillars = [
  {
    icon: Target,
    title: "Built to Convert",
    desc: "Sticky quote forms and click-to-call buttons — built around how homeowners actually hire contractors.",
    tags: ["Quote Forms", "Click-to-Call", "Lead Capture"],
  },
  {
    icon: Smartphone,
    title: "Mobile-First Speed",
    desc: "70% of home service searches happen on a phone. Every site loads fast, works clean on mobile, and keeps visitors from bouncing.",
    tags: ["Fast Load", "Responsive", "No Bounce"],
  },
  {
    icon: MapPin,
    title: "Local SEO Built-In",
    desc: "Rank for \"roofer near me.\" Show up in the map pack. Connected to Google Business so nearby homeowners find you first.",
    tags: ["Near-Me Search", "Map Pack", "Google Business"],
  },
];

const PillarCard = ({ pillar, isMobile }: { pillar: typeof pillars[0]; isMobile: boolean }) => (
  <div
    className={`flex gap-5 md:gap-6 items-start rounded-xl border border-cream/5 p-5 md:p-7 ${
      isMobile ? "bg-cream/5" : "bg-cream/5 hover:border-sage/20 transition-[border-color,transform,box-shadow] duration-300 group hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/20"
    }`}
  >
    <div className={`h-12 w-12 shrink-0 rounded-lg bg-sage/10 flex items-center justify-center ${!isMobile ? "group-hover:bg-sage/20 transition-colors duration-300" : ""}`}>
      <pillar.icon className={`h-6 w-6 text-sage ${!isMobile ? "group-hover:text-primary transition-colors duration-300" : ""}`} />
    </div>
    <div className="min-w-0">
      <h3 className="font-heading text-lg md:text-xl text-cream mb-1 font-semibold">{pillar.title}</h3>
      <p className="text-sm text-cream/60 leading-relaxed mb-3">{pillar.desc}</p>
      <div className="flex flex-wrap gap-1.5">
        {pillar.tags.map((tag) => (
          <span key={tag} className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-sage/10 text-sage/90">{tag}</span>
        ))}
      </div>
    </div>
  </div>
);

const ServicePillarsSection = () => {
  const isMobile = useIsMobile();

  return (
    <section
      id="who-we-help"
      className="lg:sticky top-0 min-h-0 lg:min-h-[100dvh] flex items-center bg-section-cool relative overflow-hidden lg:pt-[var(--navbar-height,4.75rem)]"
      style={{ zIndex: 3 }}
      data-dark-section
    >
      <SectionGlow placement="top-right" />

      <div className="container mx-auto relative z-10 py-16 md:py-20 lg:py-0">
        <div className="grid lg:grid-cols-[1fr_1.5fr] gap-4 md:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: isMobile ? 0 : 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: duration.smooth, ease: ease.smooth }}
          >
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sage/90 mb-2 md:mb-3 md:text-sm">Why It Works</p>
            <h2 className="font-heading text-2xl sm:text-4xl md:text-5xl text-cream mb-3 md:mb-4 font-bold leading-[1.1]">
              Websites that <span className="text-accent-sweep">drive</span> real results.
            </h2>
            <p className="hidden sm:block text-cream/60 leading-relaxed md:text-lg max-w-md">
              Every build is optimized for how homeowners actually search, decide, and call.
            </p>
          </motion.div>

          {isMobile ? (
            <motion.div
              className="space-y-3"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: duration.smooth, ease: ease.smooth }}
            >
              {pillars.map((pillar) => (
                <PillarCard key={pillar.title} pillar={pillar} isMobile />
              ))}
            </motion.div>
          ) : (
            <div className="space-y-4">
              {pillars.map((pillar, i) => (
                <motion.div
                  key={pillar.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ duration: duration.smooth, delay: i * stagger, ease: ease.smooth }}
                >
                  <PillarCard pillar={pillar} isMobile={false} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ServicePillarsSection;
